import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  LinearProgress,
  Alert,
  InputLabel,
  FormControl,
  TextField,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { uploadDocuments, getSignedUrl } from '../../api';

const extractS3KeyAndType = (invoice) => {
  if (!invoice) return { key: '', fileType: '' };

  if (typeof invoice === 'string') {
    const urlPattern = /^https:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/;
    const match = invoice.match(urlPattern);
    const key = match ? match[1] : '';
    const fileType = key.split('.').pop()?.toLowerCase() || '';
    return { key, fileType };
  }

  return {
    key: invoice.key || invoice.url || '',
    fileType: invoice.fileType?.toLowerCase() || invoice.url?.split('.').pop()?.toLowerCase() || '',
  };
};

const UploadDocument = ({ isOpen, onClose, formik, urlFieldName }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null); // Added preview state
  const fileInputRef = useRef(null);

  // Extract index from urlFieldName (e.g., account_details.deductions[0].invoice)
  const indexMatch = urlFieldName.match(/\[(\d+)\]/);
  const deductionIndex = indexMatch ? parseInt(indexMatch[1], 10) : null;
  const existingInvoice =
    deductionIndex !== null &&
      formik.values?.account_details?.deductions?.[deductionIndex]?.invoice
      ? formik.values.account_details.deductions[deductionIndex].invoice
      : { url: '', description: '' };

  // Initialize state based on existingInvoice
  useEffect(() => {
    setDescription(existingInvoice.description || '');
    setUploadedFileUrl(existingInvoice.url || null);
    setFile(null);
    setError(null);
    setUploadProgress(0);
    setPreviewUrl(null); // Reset preview URL

    // Cleanup on dialog close
    return () => {
      setFile(null);
      setUploadedFileUrl(null);
      setDescription('');
      setError(null);
      setUploadProgress(0);
      setPreviewUrl(null);
    };
  }, [existingInvoice.description, existingInvoice.url, isOpen]);

  // Added preview handler
  const handlePreview = async (url) => {
    try {
      const { key, fileType } = extractS3KeyAndType(url || existingInvoice.url);
      if (!key) throw new Error('Invalid file key.');
      const response = await getSignedUrl(key, 'get', fileType);
      if (response.success && response.data.signedUrl) {
        setPreviewUrl(response.data.signedUrl);
      } else {
        throw new Error('Failed to generate preview URL.');
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError(err.message || 'Failed to generate preview URL.');
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to generate preview URL.',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const handleDownload = async () => {
    try {
      const urlToDownload = uploadedFileUrl || existingInvoice.url;
      if (!urlToDownload) {
        throw new Error('No file available to download.');
      }
      const { key, fileType } = extractS3KeyAndType(urlToDownload);
      if (!key) {
        throw new Error('Invalid file key.');
      }
      const response = await getSignedUrl(key, 'get', fileType);
      if (!response.success || !response.data?.signedUrl) {
        throw new Error(response.message || 'Failed to generate download URL.');
      }
      const link = document.createElement('a');
      link.href = response.data.signedUrl;
      link.download = key.split('/').pop() || `document.${fileType || 'bin'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Failed to download file. Please try again.');
    }
  };

  const uploadToS3 = async (selectedFile) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(selectedFile.type)) {
        throw new Error('Invalid file type. Only PDF, JPEG, and PNG are allowed.');
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        throw new Error('File size exceeds 10MB limit.');
      }

      const propertyId = formik.values?.property_details?.property_id;
      if (!propertyId) {
        throw new Error('Property ID is missing.');
      }

      const response = await uploadDocuments(
        propertyId,
        [selectedFile],
        (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
          }
        }
      );

      if (!response.success || !response.data?.[0]?.fileUrl) {
        throw new Error(response.message || 'Failed to upload file.');
      }

      setUploadedFileUrl(response.data[0].fileUrl);
      setUploading(false);

      Swal.fire({
        title: 'File Uploaded',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file. Please try again.');
      setUploading(false);
      setUploadedFileUrl(null);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      uploadToS3(selectedFile);
    }
  };

  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
      uploadToS3(droppedFile);
    }
  };

  const handleSubmit = () => {
    if (uploadedFileUrl || description.trim() !== existingInvoice.description) {
      formik.setFieldValue(urlFieldName, {
        url: uploadedFileUrl || existingInvoice.url || '',
        description: description.trim() || '',
      });
    }
    onClose();
  };

  const handleCancel = () => {
    if (uploading) {
      Swal.fire({
        title: 'Cancel Upload?',
        text: 'Are you sure you want to cancel the upload?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel upload',
        cancelButtonText: 'No, continue upload',
      }).then((result) => {
        if (result.isConfirmed) {
          setUploading(false);
          setFile(null);
          setUploadedFileUrl(null);
          setDescription('');
          setError(null);
          onClose();
        }
      });
    } else {
      setFile(null);
      setUploadedFileUrl(null);
      setDescription('');
      setError(null);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={uploading ? undefined : handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 1 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload Document</Typography>
          <IconButton onClick={handleCancel} disabled={uploading}>
            <CancelIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {(existingInvoice.url || uploadedFileUrl) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Current File: {(uploadedFileUrl || existingInvoice.url).split('/').pop() || 'Unknown'}
                </Typography>
                <IconButton
                  onClick={() => handlePreview(uploadedFileUrl || existingInvoice.url)}
                  color="primary"
                  aria-label="Preview current file"
                  title="Preview"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={handleDownload}
                  color="primary"
                  aria-label="Download current file"
                  title="Download"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <FormControl>
              <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1, ml: -1 }}>
                File Upload
              </InputLabel>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'grey.400',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'grey.600' },
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                role="button"
                aria-label="Upload file"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={uploading}
                  accept="application/pdf,image/jpeg,image/png"
                />
                <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500', mb: 2 }} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {file ? file.name : 'Drag & drop file here or click to browse'}
                </Typography>
                {file && (
                  <Typography variant="body2" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                )}
              </Box>
            </FormControl>
            <FormControl>
              <TextField
                label="Description (Optional)"
                value={description}
                onChange={handleDescriptionChange}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                disabled={uploading}
                inputProps={{ maxLength: 500 }}
                aria-label="Invoice description"
              />
            </FormControl>
          </Box>

          {uploading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Uploading...</Typography>
                <Typography variant="body2">{uploadProgress}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={uploading}
            aria-label="Submit document"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog - Added this section */}
      {previewUrl && (
        <Dialog
          open={Boolean(previewUrl)}
          onClose={() => setPreviewUrl(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Document Preview</Typography>
            <IconButton onClick={() => setPreviewUrl(null)}>
              <CancelIcon fontSize="medium" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewUrl.includes('.pdf') ? (
              <iframe
                src={previewUrl}
                title="PDF Preview"
                style={{ width: '100%', height: '500px', border: 'none' }}
              />
            ) : (
              <img
                src={previewUrl}
                alt="Document Preview"
                style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download
            </Button>
            <Button onClick={() => setPreviewUrl(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export { UploadDocument };